import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Heart, LogOut, User, Moon, Sun, Inbox, MessageCircle, BarChart3, Users, Crown, Settings, ShieldCheck, Languages, Download, Sparkles,
} from "lucide-react";
import NotificationBell from "@/components/umaang/NotificationBell";

export default function Nav() {
  const { user, logout } = useAuth();
  const { t, lang, setLang, languages } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const loc = useLocation();
  const nav = useNavigate();

  const link = (to, label, testId) => (
    <Link
      to={to}
      data-testid={testId}
      className={`text-sm px-3 py-2 rounded-full transition-colors ${
        loc.pathname === to
          ? "bg-[#EAE6DF] dark:bg-neutral-800 text-[color:var(--umg-text)]"
          : "text-[color:var(--umg-text-2)] hover:text-[color:var(--umg-text)] hover:bg-[#EAE6DF]/60 dark:hover:bg-neutral-800"
      }`}
    >
      {label}
    </Link>
  );

  // Colorful accent per menu item
  const MOBILE_ITEMS = [
    { to: "/home",      label: "Home",       icon: Heart,         color: "#C85A53", tid: "menu-home" },
    { to: "/inbox",     label: "Inbox",      icon: Inbox,         color: "#2563EB", tid: "menu-inbox" },
    { to: "/inbox",  label: "Messages",   icon: MessageCircle, color: "#7C3AED", tid: "menu-messages" },
    { to: "/ai-assistant", label: "AI Sahayak", icon: Sparkles,   color: "#7C3AED", tid: "menu-ai" },
    { to: "/insights",  label: "Insights",   icon: BarChart3,     color: "#059669", tid: "menu-insights" },
    { to: "/referrals", label: "Refer",      icon: Users,         color: "#F59E0B", tid: "menu-refer" },
    { to: "/premium",   label: "Premium",    icon: Crown,         color: "#B45309", tid: "menu-premium" },
  ];

  return (
    <nav
      data-testid="main-nav"
      className="sticky top-0 z-40 bg-[color:var(--umg-bg)]/85 backdrop-blur-xl border-b border-[color:var(--umg-border)]"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <img
            src="/logo-full.png"
            alt="Umaang"
            className="h-10 md:h-11 w-auto object-contain select-none"
            draggable="false"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          {user && link("/home", t("home"), "nav-home")}
          {user && link("/inbox", t("messages"), "nav-messages")}
          {user && link("/insights", t("insights"), "nav-insights")}
          {user && link("/referrals", "Refer", "nav-referrals")}
          {user && link("/premium", t("premium"), "nav-premium")}
          {user?.role === "admin" && link("/admin", t("admin"), "nav-admin")}
        </div>

        <div className="flex items-center gap-1.5 md:gap-2">
          <Link
            to="/install"
            data-testid="nav-install-app"
            className="hidden md:inline-flex items-center gap-1.5 text-sm text-[color:var(--umg-text-2)] hover:text-[#DB2777] px-2 h-8 rounded-full"
          >
            <Download className="w-4 h-4" /> Install
          </Link>
          {user && <NotificationBell />}
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-[#EAE6DF] dark:hover:bg-neutral-800 transition"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {!user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="lang-toggle"
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 border border-[color:var(--umg-border)] rounded-full hover:bg-[#EAE6DF] dark:hover:bg-neutral-800 transition"
                  aria-label="Change language"
                >
                  <Languages className="w-3.5 h-3.5" /> {lang.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 max-h-80 overflow-y-auto">
                {languages.map((L) => (
                  <DropdownMenuItem
                    key={L.code}
                    data-testid={`lang-opt-${L.code}`}
                    onClick={() => setLang(L.code)}
                    className={`cursor-pointer ${lang === L.code ? "bg-[color:var(--umg-secondary)] font-semibold" : ""}`}
                  >
                    <span className="w-8 text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)]">{L.code}</span>
                    <span className="flex-1">{L.native}</span>
                    <span className="text-[10px] text-[color:var(--umg-text-2)]">{L.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {user ? (
            <>
              <Link
                to={user?.id ? `/profile/${user.id}` : "/home"}
                data-testid="nav-my-profile"
                className="hidden md:inline-flex items-center gap-1.5 text-sm text-[color:var(--umg-text-2)] hover:text-[color:var(--umg-text)]"
              >
                <User className="w-4 h-4" /> {user.name}
              </Link>

              {/* Mobile: colorful profile icon → dropdown menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="mobile-profile-menu"
                    className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#C85A53] to-[#A94842] text-white shadow-md shadow-[#C85A53]/40 active:scale-95 transition"
                    aria-label="Open menu"
                  >
                    <User className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 mt-1">
                  <DropdownMenuLabel className="flex items-center gap-2 py-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C85A53] to-[#A94842] grid place-items-center text-white text-xs font-bold">
                      {(user.name || "?")[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{user.name}</div>
                      <div className="text-[10px] text-[color:var(--umg-text-2)] truncate">{user.email}</div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {MOBILE_ITEMS.map((item) => (
                    <DropdownMenuItem key={item.to} asChild>
                      <Link to={item.to} data-testid={item.tid} className="cursor-pointer">
                        <span className="w-7 h-7 rounded-lg grid place-items-center mr-2" style={{ background: `${item.color}18`, color: item.color }}>
                          <item.icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-sm">{item.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={user?.id ? `/profile/${user.id}` : "/home"} data-testid="menu-my-profile" className="cursor-pointer">
                      <span className="w-7 h-7 rounded-lg grid place-items-center mr-2" style={{ background: "#7C3AED18", color: "#7C3AED" }}>
                        <User className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-sm">My profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile/edit" data-testid="menu-edit-profile" className="cursor-pointer">
                      <span className="w-7 h-7 rounded-lg grid place-items-center mr-2" style={{ background: "#1C191718", color: "#1C1917" }}>
                        <Settings className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-sm">Edit profile</span>
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" data-testid="menu-admin" className="cursor-pointer">
                        <span className="w-7 h-7 rounded-lg grid place-items-center mr-2" style={{ background: "#DC262618", color: "#DC2626" }}>
                          <ShieldCheck className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-sm">Admin</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger data-testid="menu-language" className="cursor-pointer">
                      <span className="w-7 h-7 rounded-lg grid place-items-center mr-2" style={{ background: "#0EA5E918", color: "#0EA5E9" }}>
                        <Languages className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-sm flex-1">{t("language")}</span>
                      <span className="text-[10px] text-[color:var(--umg-text-2)] uppercase tracking-widest">{lang}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-56 max-h-80 overflow-y-auto">
                      {languages.map((L) => (
                        <DropdownMenuItem
                          key={L.code}
                          data-testid={`menu-lang-${L.code}`}
                          onClick={() => setLang(L.code)}
                          className={`cursor-pointer ${lang === L.code ? "bg-[color:var(--umg-secondary)] font-semibold" : ""}`}
                        >
                          <span className="w-8 text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)]">{L.code}</span>
                          <span className="flex-1">{L.native}</span>
                          <span className="text-[10px] text-[color:var(--umg-text-2)]">{L.label}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuItem asChild>
                    <Link to="/install" data-testid="menu-install-app" className="cursor-pointer">
                      <span className="w-7 h-7 rounded-lg grid place-items-center mr-2" style={{ background: "#DB277718", color: "#DB2777" }}>
                        <Download className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-sm">Install app</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-logout"
                    onClick={() => { logout(); nav("/"); }}
                    className="cursor-pointer text-[#C85A53]"
                  >
                    <span className="w-7 h-7 rounded-lg grid place-items-center mr-2" style={{ background: "#C85A5318", color: "#C85A53" }}>
                      <LogOut className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-sm">Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                data-testid="nav-logout-btn"
                onClick={() => { logout(); nav("/"); }}
                variant="ghost"
                size="sm"
                className="hidden md:inline-flex rounded-full"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/auth" data-testid="nav-login">
                <Button variant="ghost" className="rounded-full text-sm px-3">{t("login")}</Button>
              </Link>
              <Link to="/auth?mode=register" data-testid="nav-register">
                <Button className="rounded-full bg-gradient-to-r from-[#C85A53] to-[#A94842] hover:opacity-90 text-white text-sm px-4">
                  {t("register")}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
